import React, { useEffect, useState } from 'react';
import { default as Select, components } from 'react-select';
import { formatTestId } from '../../util/EfupUtility';
import { DropdownIndicator } from '../CustomDropdownIndicator/CustomDropdownIndicator'

import './DropdownMultiSelect.css';

// Declare custom components outside of Select render to prevent unexpected behavior
const Control = ({ children, ...props }) => {
    const label = props.selectProps.customProps.multiSelectLabel;
    const type = props.selectProps.customProps.selectType;
    const isMobile = props.selectProps.customProps.isMobile;
    const labelTestId = formatTestId(`${type}-filter-control-label`)
    const valuesTestId = formatTestId(`${type}-filter-control-values`)

    return (<components.Control {...props}>
        {isMobile?<></>:
            <div 
                className={`efup-filter-label cb-font-poppins efup-${type}-filter-label`}
                data-testid={labelTestId}
            >{label}:</div>
        }
        <div className='efup-filter-value-container' data-testid={valuesTestId}>{children}</div>
    </components.Control>);
};

const MultiValueContainer = ({ children, ...props }) => {
    const selectType = props.selectProps.customProps.selectType;
    const selectedOptions = props.selectProps.value;
    // Hide other options if all is selected
    if (selectedOptions.some(option => option.value === 'all')) {
        if (props.data.value === 'all') {
            return (
                <components.MultiValueContainer {...props}>
                    <div className={`cb-font-poppins efup-${selectType}-option-display`}>{props.data.label}</div>
                </components.MultiValueContainer>
            );
        }
        return <React.Fragment />;
    } else {
        const selectedValues = props.selectProps.value;
        if (selectedValues[selectedValues.length - 1] === props.data) {
            return (
                <components.MultiValueContainer {...props}>
                    <div className={`cb-font-poppins efup-${selectType}-option-display`}>({selectedValues.length})</div>
                </components.MultiValueContainer>
            );
        }
        return <React.Fragment />;
    }
};

const Option = (props) => {
    const selectType = props.selectProps.customProps.selectType;
    const testId = formatTestId(`${selectType}-${props.label}-option`)

    return (
        <components.Option {...props} className={`efup-dropdown-option efup-${selectType}-dropdown-option`}>
            <input
                type="checkbox"
                checked={props.isSelected}
                onChange={() => null}
                data-testid={testId}
            />
            <span className="checkmark"></span>{" "}
            <label>{props.label}</label>
        </components.Option>
    );
};

export default function DropdownMultiSelect(props) {
    const multiSelectOptions = props.options;
    const multiSelectLabel = props.label;
    const selectType = props.type;
    const onChange = props.onChange;
    const [selectedOptions, setSelectedOptions] = useState(props.selectedOptions);

    useEffect(() => {
        if (onChange) {
            onChange(selectedOptions);
        }
    }, [selectedOptions]);     // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setSelectedOptions(props.selectedOptions);
    }, [props.selectedOptions]);

    const customStyles = {
        menu: (provided) => ({
            ...provided,
            width: 'max-content',
            minWidth: '100%',
            marginTop: '5px'
        }),
        control: (styles, { isDisabled, isFocused, isSelected }) => {
            return {
                ...styles,
                boxShadow: isFocused ? '0 0 0 1px #00b5e2' : undefined,
                borderColor: isDisabled
                    ? undefined
                    : isSelected && !isFocused
                        ? '#00b5e2'
                        : isFocused
                            ? '#00b5e2'
                            : '#D3D6DE',
                ':hover': {
                    borderColor: '#00b5e2',
                }
            }
        },
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: 'white'
        }),
        option: (styles, { isDisabled, isFocused, isSelected }) => {
            return {
                ...styles,
                backgroundColor: isDisabled
                    ? undefined
                    : isSelected && !isFocused
                        ? 'transparent'
                        : isFocused
                            ? '#00b5e2'
                            : undefined,
                cursor: isDisabled ? 'not-allowed' : 'default',

                ':active': {
                    ...styles[':active'],
                    backgroundColor: !isDisabled
                        ? isSelected
                            ? '#00b5e2'
                            : '#00b5e2'
                        : undefined,
                },
                ':hover': {
                    backgroundColor: !isDisabled
                        ? isSelected
                            ? '#00b5e2'
                            : '#00b5e2'
                        : undefined,
                }
            };
        },
    }

    function handleListChange(event, selected) {
        let options = event;
        if (selected.action === 'deselect-option') {
            // Remove all selections if the all option is deselected
            if (selected.option.value === 'all') {
                options = [];
            } else {
                // Remove the all option if any field is deselected
                options = event.filter(option => option.value !== 'all');
            }
        } else if (selected.action === 'select-option') {
            if (options.length > multiSelectOptions.length - 2 || selected.option.value === 'all') {
                options = multiSelectOptions;
            }
        }
        setSelectedOptions(options);
    }

    return (
        <Select
            placeholder={props.isMobile? `Select a ${multiSelectLabel}`: `Please Select a ${multiSelectLabel}`}
            className="cb-multiselect cb-form"
            options={multiSelectOptions}
            defaultValue={selectedOptions}
            components={{
                IndicatorSeparator: () => null,
                Option,
                MultiValueContainer,
                Control,
                DropdownIndicator
            }}
            isClearable={false}
            isSearchable={false}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            customProps={{ multiSelectLabel: multiSelectLabel, selectType: selectType, isMobile: props.isMobile }}
            styles={customStyles}
            value={selectedOptions}
            onChange={handleListChange}
            isMulti
        />
    );
}